// TIBECO - Calculator Preț Ferestre și Uși
import React, { useState, useEffect } from 'react';
import { 
  Calculator, 
  Ruler, 
  Layers, 
  Palette, 
  Shield, 
  Zap,
  Info,
  Download,
  Mail,
  Plus,
  Minus,
  Settings
} from 'lucide-react';
import Card from '../../components/Card';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';

const PriceCalculator = () => {
  const [tipProdus, setTipProdus] = useState('fereastra');
  const [configuratie, setConfiguratie] = useState({
    latime: 120,
    inaltime: 140,
    culoare: 'alb',
    profile: 'pvc_5_camere',
    geam: 'dublu',
    feronerie: 'standard',
    manere: 'standard',
    cant: 1
  });
  
  const [pretCalculat, setPretCalculat] = useState(0);
  const [showOfertaModal, setShowOfertaModal] = useState(false);
  const [clientData, setClientData] = useState({
    nume: '',
    telefon: '',
    email: '',
    adresa: ''
  });

  // Prețuri bază TIBECO (în RON)
  const preturiProduse = {
    fereastra: {
      baza: 180, // RON per m²
      profile: {
        'pvc_3_camere': 0,
        'pvc_5_camere': 25,
        'pvc_7_camere': 45,
        'aluminiu_standard': 60,
        'aluminiu_termopan': 85
      },
      geam: {
        'simplu': 0,
        'dublu': 35,
        'triplu': 75,
        'triplu_low_e': 95
      },
      culoare: {
        'alb': 0,
        'maro': 15,
        'antracit': 25,
        'stejar': 30,
        'nuc': 30
      },
      feronerie: {
        'standard': 0,
        'premium': 45,
        'securitate': 65
      },
      manere: {
        'standard': 0,
        'premium': 25,
        'smart': 85
      }
    },
    usa: {
      baza: 850, // RON per bucată
      material: {
        'pvc_standard': 0,
        'pvc_premium': 150,
        'aluminiu': 200,
        'lemn': 300
      },
      securitate: {
        'standard': 0,
        'antiefractie': 250,
        'blindata': 450
      },
      sticla: {
        'fara': 0,
        'simpla': 75,
        'securizata': 125,
        'decorativa': 180
      }
    }
  };

  // Calculare preț automat
  useEffect(() => {
    calculeazaPret();
  }, [configuratie, tipProdus]);

  const calculeazaPret = () => {
    let pret = 0;
    
    if (tipProdus === 'fereastra') {
      // Suprafața în m²
      const suprafata = (configuratie.latime / 100) * (configuratie.inaltime / 100);
      
      // Preț bază per m²
      pret = preturiProduse.fereastra.baza * suprafata;
      
      // Adaugă costurile pentru profile
      pret += preturiProduse.fereastra.profile[configuratie.profile] * suprafata;
      
      // Adaugă costurile pentru geam
      pret += preturiProduse.fereastra.geam[configuratie.geam] * suprafata;
      
      // Adaugă costurile pentru culoare
      pret += preturiProduse.fereastra.culoare[configuratie.culoare] * suprafata;
      
      // Adaugă costurile pentru feronerie (per bucată)
      pret += preturiProduse.fereastra.feronerie[configuratie.feronerie];
      
      // Adaugă costurile pentru mânere (per bucată)
      pret += preturiProduse.fereastra.manere[configuratie.manere];
      
    } else {
      // Preț bază pentru ușă
      pret = preturiProduse.usa.baza;
      
      // Adaugă materialul
      if (configuratie.material) {
        pret += preturiProduse.usa.material[configuratie.material];
      }
      
      // Adaugă securitatea
      if (configuratie.securitate) {
        pret += preturiProduse.usa.securitate[configuratie.securitate];
      }
      
      // Adaugă sticla
      if (configuratie.sticla) {
        pret += preturiProduse.usa.sticla[configuratie.sticla];
      }
    }
    
    // Multiplică cu cantitatea
    pret = pret * configuratie.cant;
    
    // Adaugă montaj (15% din preț)
    pret = pret * 1.15;
    
    setPretCalculat(Math.round(pret));
  };

  const handleConfigChange = (key, value) => {
    setConfiguratie(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const genereazaOferta = () => {
    setShowOfertaModal(true);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Calculator Preț TIBECO
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Calculează prețul pentru ferestre și uși PVC/Aluminiu cu configurarea dorită
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Configurator */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Selecție tip produs */}
            <Card className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Tipul Produsului
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setTipProdus('fereastra')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    tipProdus === 'fereastra' 
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30' 
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <div className="flex flex-col items-center space-y-2">
                    <Layers className="w-8 h-8 text-primary-600" />
                    <span className="font-medium text-gray-900 dark:text-white">
                      Fereastră
                    </span>
                  </div>
                </button>
                
                <button
                  onClick={() => setTipProdus('usa')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    tipProdus === 'usa' 
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30' 
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <div className="flex flex-col items-center space-y-2">
                    <Shield className="w-8 h-8 text-primary-600" />
                    <span className="font-medium text-gray-900 dark:text-white">
                      Ușă
                    </span>
                  </div>
                </button>
              </div>
            </Card>

            {/* Dimensiuni */}
            {tipProdus === 'fereastra' && (
              <Card className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  <Ruler className="w-5 h-5 inline mr-2" />
                  Dimensiuni (cm)
                </h2>
                
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Lățime
                    </label>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleConfigChange('latime', Math.max(60, configuratie.latime - 10))}
                        className="p-2 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <input
                        type="number"
                        value={configuratie.latime}
                        onChange={(e) => handleConfigChange('latime', parseInt(e.target.value))}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-center bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        min="60"
                        max="300"
                      />
                      <button
                        onClick={() => handleConfigChange('latime', Math.min(300, configuratie.latime + 10))}
                        className="p-2 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Înălțime
                    </label>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleConfigChange('inaltime', Math.max(80, configuratie.inaltime - 10))}
                        className="p-2 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <input
                        type="number"
                        value={configuratie.inaltime}
                        onChange={(e) => handleConfigChange('inaltime', parseInt(e.target.value))}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-center bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        min="80"
                        max="250"
                      />
                      <button
                        onClick={() => handleConfigChange('inaltime', Math.min(250, configuratie.inaltime + 10))}
                        className="p-2 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    <Info className="w-4 h-4 inline mr-1" />
                    Suprafață calculată: <strong>{((configuratie.latime / 100) * (configuratie.inaltime / 100)).toFixed(2)} m²</strong>
                  </p>
                </div>
              </Card>
            )}

            {/* Configurații pentru Ferestre */}
            {tipProdus === 'fereastra' && (
              <>
                <Card className="p-6">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    Configurație Tehnică
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Tip Profile
                      </label>
                      <select
                        value={configuratie.profile}
                        onChange={(e) => handleConfigChange('profile', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      >
                        <option value="pvc_3_camere">PVC 3 camere</option>
                        <option value="pvc_5_camere">PVC 5 camere (+25 RON/m²)</option>
                        <option value="pvc_7_camere">PVC 7 camere (+45 RON/m²)</option>
                        <option value="aluminiu_standard">Aluminiu Standard (+60 RON/m²)</option>
                        <option value="aluminiu_termopan">Aluminiu Termopan (+85 RON/m²)</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Tip Geam
                      </label>
                      <select
                        value={configuratie.geam}
                        onChange={(e) => handleConfigChange('geam', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      >
                        <option value="simplu">Geam simplu</option>
                        <option value="dublu">Geam dublu (+35 RON/m²)</option>
                        <option value="triplu">Geam triplu (+75 RON/m²)</option>
                        <option value="triplu_low_e">Triplu Low-E (+95 RON/m²)</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Feronerie
                      </label>
                      <select
                        value={configuratie.feronerie}
                        onChange={(e) => handleConfigChange('feronerie', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      >
                        <option value="standard">Standard</option>
                        <option value="premium">Premium (+45 RON)</option>
                        <option value="securitate">Securitate (+65 RON)</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Mânere
                      </label>
                      <select
                        value={configuratie.manere}
                        onChange={(e) => handleConfigChange('manere', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      >
                        <option value="standard">Standard</option>
                        <option value="premium">Premium (+25 RON)</option>
                        <option value="smart">Smart Lock (+85 RON)</option>
                      </select>
                    </div>
                  </div>
                </Card>

                <Card className="p-6">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    <Palette className="w-5 h-5 inline mr-2" />
                    Culoare Profile
                  </h2>
                  
                  <div className="grid grid-cols-5 gap-3">
                    {[
                      { key: 'alb', label: 'Alb', color: '#ffffff', border: true },
                      { key: 'maro', label: 'Maro', color: '#8B4513' },
                      { key: 'antracit', label: 'Antracit', color: '#2F4F4F' },
                      { key: 'stejar', label: 'Stejar', color: '#D2B48C' },
                      { key: 'nuc', label: 'Nuc', color: '#8B4513' }
                    ].map((culoare) => (
                      <button
                        key={culoare.key}
                        onClick={() => handleConfigChange('culoare', culoare.key)}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          configuratie.culoare === culoare.key 
                            ? 'border-primary-500 shadow-lg' 
                            : 'border-gray-200 dark:border-gray-700'
                        }`}
                      >
                        <div 
                          className={`w-8 h-8 rounded-full mx-auto mb-1 ${culoare.border ? 'border border-gray-300' : ''}`}
                          style={{ backgroundColor: culoare.color }}
                        />
                        <span className="text-xs text-gray-600 dark:text-gray-400">
                          {culoare.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </Card>
              </>
            )}

            {/* Configurații pentru Uși */}
            {tipProdus === 'usa' && (
              <Card className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Configurație Ușă
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Material
                    </label>
                    <select
                      value={configuratie.material || 'pvc_standard'}
                      onChange={(e) => handleConfigChange('material', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    >
                      <option value="pvc_standard">PVC Standard</option>
                      <option value="pvc_premium">PVC Premium (+150 RON)</option>
                      <option value="aluminiu">Aluminiu (+200 RON)</option>
                      <option value="lemn">Lemn masiv (+300 RON)</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Nivel Securitate
                    </label>
                    <select
                      value={configuratie.securitate || 'standard'}
                      onChange={(e) => handleConfigChange('securitate', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    >
                      <option value="standard">Standard</option>
                      <option value="antiefractie">Anti-efracție (+250 RON)</option>
                      <option value="blindata">Blindată (+450 RON)</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Sticlă
                    </label>
                    <select
                      value={configuratie.sticla || 'fara'}
                      onChange={(e) => handleConfigChange('sticla', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    >
                      <option value="fara">Fără sticlă</option>
                      <option value="simpla">Sticlă simplă (+75 RON)</option>
                      <option value="securizata">Sticlă securizată (+125 RON)</option>
                      <option value="decorativa">Sticlă decorativă (+180 RON)</option>
                    </select>
                  </div>
                </div>
              </Card>
            )}

            {/* Cantitate */}
            <Card className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Cantitate
              </h2>
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => handleConfigChange('cant', Math.max(1, configuratie.cant - 1))}
                  className="p-2 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <input
                  type="number"
                  value={configuratie.cant}
                  onChange={(e) => handleConfigChange('cant', Math.max(1, parseInt(e.target.value)))}
                  className="w-20 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-center bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  min="1"
                />
                <button
                  onClick={() => handleConfigChange('cant', configuratie.cant + 1)}
                  className="p-2 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  <Plus className="w-4 h-4" />
                </button>
                <span className="text-gray-600 dark:text-gray-400 ml-4">
                  bucă{configuratie.cant === 1 ? 'ță' : 'ți'}
                </span>
              </div>
            </Card>
          </div>

          {/* Rezultat și Comandă */}
          <div className="space-y-6">
            <Card className="p-6 sticky top-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                <Calculator className="w-5 h-5 inline mr-2" />
                Preț Calculat
              </h2>
              
              <div className="text-center mb-6">
                <div className="text-4xl font-bold text-primary-600 mb-2">
                  {pretCalculat.toLocaleString()} RON
                </div>
                {configuratie.cant > 1 && (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    ({Math.round(pretCalculat / configuratie.cant).toLocaleString()} RON / bucată)
                  </p>
                )}
                <Badge variant="success" className="mt-2">
                  Include montaj
                </Badge>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Preț material:</span>
                  <span className="text-gray-900 dark:text-white">
                    {Math.round(pretCalculat / 1.15).toLocaleString()} RON
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Montaj (15%):</span>
                  <span className="text-gray-900 dark:text-white">
                    {Math.round(pretCalculat - (pretCalculat / 1.15)).toLocaleString()} RON
                  </span>
                </div>
                <hr className="border-gray-200 dark:border-gray-700" />
                <div className="flex justify-between font-semibold">
                  <span className="text-gray-900 dark:text-white">Total:</span>
                  <span className="text-primary-600">{pretCalculat.toLocaleString()} RON</span>
                </div>
              </div>

              <div className="space-y-3">
                <button 
                  onClick={genereazaOferta}
                  className="w-full flex items-center justify-center px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Trimite Ofertă
                </button>
                
                <button className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <Download className="w-4 h-4 mr-2" />
                  Descarcă PDF
                </button>
              </div>

              <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/30 rounded-lg">
                <h4 className="font-semibold text-green-800 dark:text-green-200 mb-2">
                  <Zap className="w-4 h-4 inline mr-1" />
                  Garanție TIBECO
                </h4>
                <ul className="text-sm text-green-700 dark:text-green-300 space-y-1">
                  <li>• 10 ani garanție profile</li>
                  <li>• 2 ani garanție montaj</li>
                  <li>• Service gratuit primul an</li>
                  <li>• Măsurătoare gratuită</li>
                </ul>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Modal Ofertă */}
      <Modal isOpen={showOfertaModal} onClose={() => setShowOfertaModal(false)}>
        <div className="p-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            Trimite Oferta de Preț
          </h2>
          
          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Nume complet
              </label>
              <input
                type="text"
                value={clientData.nume}
                onChange={(e) => setClientData(prev => ({ ...prev, nume: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                placeholder="ex: Popescu Ion"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Telefon
                </label>
                <input
                  type="tel"
                  value={clientData.telefon}
                  onChange={(e) => setClientData(prev => ({ ...prev, telefon: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  placeholder="+40 722 123 456"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={clientData.email}
                  onChange={(e) => setClientData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  placeholder="ion@email.com"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Adresa montaj
              </label>
              <input
                type="text"
                value={clientData.adresa}
                onChange={(e) => setClientData(prev => ({ ...prev, adresa: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                placeholder="Str. Florilor 25, Suceava"
              />
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg mb-6">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
              Sumar Comandă
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
              {tipProdus === 'fereastra' 
                ? `${configuratie.cant}x Ferestre ${configuratie.latime}x${configuratie.inaltime} cm`
                : `${configuratie.cant}x Uși ${configuratie.material || 'PVC'}`
              }
            </p>
            <p className="text-lg font-bold text-primary-600">
              Total: {pretCalculat.toLocaleString()} RON
            </p>
          </div>

          <div className="flex justify-end space-x-3">
            <button 
              onClick={() => setShowOfertaModal(false)}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Anulează
            </button>
            <button className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
              Trimite Oferta
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PriceCalculator;